import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Download, Eye, MessageSquareText, Send, CheckCircle2, GraduationCap, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { tpError } from '@/lib/tpErrors';
import { toast } from 'sonner';
import { PerfProfileView, dimLabel } from '@/components/client/PerfProfileView';

const ACTION_TYPES = ['training', 'coaching', 'mentoring', 'job_exposure', 'process_improvement', 'behavioral', 'technical', 'leadership', 'follow_up', 'other'];

export function PerfEvaluationDetail({ cycleId, evaluationId, cycleStatus, onClose }) {
  const { t, i18n } = useTranslation();
  const es = i18n.language?.startsWith('es');
  const base = `/client/talentperformance/cycles/${cycleId}/evaluations/${evaluationId}`;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [raw, setRaw] = useState(null);
  const [fbNote, setFbNote] = useState('');
  const [planOpen, setPlanOpen] = useState(false);
  const [plan, setPlan] = useState({ dimension: '', objective: '', action: '', action_type: 'training', responsible: '', target_date: '' });

  const load = useCallback(() => {
    api.get(base).then((r) => setData(r.data)).catch((e) => { toast.error(tpError(t, e)); onClose(); });
  }, [base, onClose, t]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const results = data.results;

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); }
    catch (e) { toast.error(tpError(t, e)); }
    finally { setBusy(false); }
  };

  const download = async (url, name, params = {}) => {
    try {
      const r = await api.get(url, { params: { lang: es ? 'es' : 'en', ...params }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = name; a.click();
    } catch (e) { toast.error(tpError(t, e)); }
  };

  const viewRaw = async (front) => {
    try {
      const r = await api.get(`${base}/responses/${front}`);
      setRaw({ front, ...r.data });
    } catch (e) { toast.error(tpError(t, e)); }
  };

  const dims = Object.keys(results?.consolidated?.dimensions || {});

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-[94vw] xl:max-w-[1500px] h-[94vh] overflow-y-auto p-6 sm:p-8" data-testid="tp-eval-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {data.snapshot?.user_name}
            <Badge variant="outline">{t(`talentperformance.evalStatus.${data.status}`, { defaultValue: data.status })}</Badge>
            {results && (
              <span className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => download(`${base}/export/pdf`, 'performance-profile.pdf')} data-testid="tp-export-pdf-top">
                  <Download className="h-3.5 w-3.5" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => download(`${base}/export/docx`, 'performance-profile.docx')} data-testid="tp-export-docx-top">
                  <Download className="h-3.5 w-3.5" /> Word
                </Button>
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {data.snapshot?.position || ''}{data.snapshot?.department_name ? ` · ${data.snapshot.department_name}` : ''}
            {data.supervisor_name ? ` · ${t('talentperformance.fields.supervisor', { defaultValue: 'Supervisor' })}: ${data.supervisor_name}` : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Front assignment status */}
        <section className="space-y-2">
          <h4 className="text-sm font-semibold">{t('talentperformance.eval.frontsStatus', { defaultValue: 'Evaluation fronts' })}</h4>
          <div className="flex flex-wrap gap-2" data-testid="tp-front-status">
            {(data.assignments || []).map((a, i) => (
              <Badge key={i} variant="outline" className={a.status === 'submitted' ? 'bg-success/10 text-success border-success/20' : ''}>
                {t(`talentperformance.fronts.${a.front}`)}{a.front !== 'internal_client' ? '' : ` ${i + 1}`} · {a.status === 'submitted' ? t('talentperformance.eval.submitted', { defaultValue: 'submitted' }) : t('talentperformance.eval.pending', { defaultValue: 'pending' })}
              </Badge>
            ))}
            {(data.assignments || []).length === 0 && <p className="text-xs text-muted-foreground">{t('talentperformance.eval.notSent', { defaultValue: 'Evaluations are sent when the cycle is activated.' })}</p>}
          </div>
          {results && (
            <div className="flex gap-2 flex-wrap">
              {['self', 'supervisor', 'internal_client'].map((f) => (
                (data.assignments || []).some((a) => a.front === f && a.status === 'submitted') && (
                  <Button key={f} size="sm" variant="ghost" className="gap-1.5 h-7 text-xs" onClick={() => viewRaw(f)} data-testid={`tp-raw-${f}`}>
                    <Eye className="h-3 w-3" />{t('talentperformance.eval.rawOf', { defaultValue: 'Responses' })}: {t(`talentperformance.fronts.${f}`)}
                  </Button>
                )
              ))}
            </div>
          )}
        </section>

        <Separator />

        {results ? <PerfProfileView results={results} evaluation={data} /> : (
          <p className="text-sm text-muted-foreground" data-testid="tp-no-results">{t('talentperformance.eval.noResults', { defaultValue: 'Results will appear as evaluations are submitted.' })}</p>
        )}

        {results && (
          <>
            <Separator />
            {/* Feedback + release */}
            <section className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2"><MessageSquareText className="h-4 w-4" />{t('talentperformance.eval.feedback', { defaultValue: 'Feedback conversation' })}</h4>
              {data.feedback?.done ? (
                <p className="text-sm text-success flex items-center gap-1.5" data-testid="tp-feedback-done">
                  <CheckCircle2 className="h-4 w-4" />{t('talentperformance.eval.feedbackDone', { defaultValue: 'Conversation recorded' })} · {data.feedback.by_name}
                  {data.feedback.note && <span className="text-muted-foreground"> — {data.feedback.note}</span>}
                </p>
              ) : (
                <div className="flex gap-2 items-start flex-wrap">
                  <Textarea rows={2} value={fbNote} onChange={(e) => setFbNote(e.target.value)} className="flex-1 min-w-[220px]"
                    placeholder={t('talentperformance.eval.feedbackPh', { defaultValue: 'Optional note about the performance conversation…' })} data-testid="tp-feedback-note" />
                  <Button size="sm" disabled={busy} onClick={() => act(() => api.post(`${base}/feedback`, { note: fbNote || null }), t('talentperformance.eval.feedbackOk', { defaultValue: 'Feedback recorded.' }))} data-testid="tp-feedback-btn">
                    {t('talentperformance.eval.recordFeedback', { defaultValue: 'Record conversation' })}
                  </Button>
                </div>
              )}
              <div className="flex gap-2 flex-wrap items-center">
                {data.released ? (
                  <Badge variant="outline" className="bg-success/10 text-success border-success/20" data-testid="tp-released-badge">
                    {t('talentperformance.eval.released', { defaultValue: 'Released to employee' })}
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1.5" disabled={busy}
                    onClick={() => act(() => api.post(`${base}/release`), t('talentperformance.eval.releasedOk', { defaultValue: 'Results released to the employee.' }))} data-testid="tp-release-btn">
                    <Send className="h-3.5 w-3.5" />{t('talentperformance.eval.release', { defaultValue: 'Release results to employee' })}
                  </Button>
                )}
                {data.acknowledgement?.at && (
                  <Badge variant="outline" data-testid="tp-ack-badge">
                    {t(`talentperformance.ack.${data.acknowledgement.status}`, { defaultValue: data.acknowledgement.status })}
                    {data.acknowledgement.agree === true ? ` · ${t('talentperformance.ack.agree', { defaultValue: 'agrees' })}` : data.acknowledgement.agree === false ? ` · ${t('talentperformance.ack.disagree', { defaultValue: 'disagrees' })}` : ''}
                  </Badge>
                )}
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => download(`${base}/export/pdf`, 'performance-profile.pdf')} data-testid="tp-export-pdf">
                  <Download className="h-3.5 w-3.5" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => download(`${base}/export/docx`, 'performance-profile.docx')} data-testid="tp-export-docx">
                  <Download className="h-3.5 w-3.5" /> Word
                </Button>
              </div>
              {data.acknowledgement?.comment && (
                <p className="text-xs text-muted-foreground" data-testid="tp-ack-comment"><b>{t('talentperformance.ack.comment', { defaultValue: 'Employee comment' })}:</b> {data.acknowledgement.comment}</p>
              )}
            </section>

            <Separator />
            {/* Development plan */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold flex items-center gap-2"><GraduationCap className="h-4 w-4" />{t('talentperformance.plan.title', { defaultValue: 'Development plan' })}</h4>
                <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={() => setPlanOpen(true)} data-testid="tp-plan-add">
                  <Plus className="h-3.5 w-3.5" />{t('talentperformance.plan.add', { defaultValue: 'Add action' })}
                </Button>
              </div>
              {(data.development_plan || []).length === 0 ? (
                <p className="text-xs text-muted-foreground italic">{t('talentperformance.plan.empty', { defaultValue: 'No development actions yet.' })}</p>
              ) : (data.development_plan || []).map((it) => (
                <div key={it.id} className="rounded border border-border/60 px-3 py-2 text-sm flex items-start justify-between gap-3" data-testid={`tp-plan-item-${it.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium">{it.objective}</p>
                    <p className="text-xs text-muted-foreground">
                      {it.dimension ? `${dimLabel(t, it.dimension)} · ` : ''}{t(`talentperformance.plan.types.${it.action_type}`, { defaultValue: it.action_type })} · {it.action}
                      {it.responsible ? ` · ${it.responsible}` : ''}{it.target_date ? ` · ${it.target_date}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline">{t(`talentperformance.plan.statuses.${it.status}`, { defaultValue: it.status })}</Badge>
                    {it.sent_to_talenttrain ? (
                      <Badge variant="outline" className="bg-success/10 text-success border-success/20">TalentTrain ✓</Badge>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1" disabled={busy}
                        onClick={() => act(() => api.post(`${base}/plan/${it.id}/send-to-talenttrain`), t('talentperformance.plan.sentOk', { defaultValue: 'Sent to TalentTrain.' }))}
                        data-testid={`tp-plan-send-${it.id}`}>
                        <Send className="h-3 w-3" />{t('talentperformance.plan.sendToTT', { defaultValue: 'Send to TalentTrain' })}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </section>
          </>
        )}

        {/* Raw responses dialog */}
        <Dialog open={!!raw} onOpenChange={(o) => { if (!o) setRaw(null); }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="tp-raw-dialog">
            <DialogHeader>
              <DialogTitle>{raw ? t(`talentperformance.fronts.${raw.front}`) : ''}</DialogTitle>
              <DialogDescription>{t('talentperformance.eval.rawDesc', { defaultValue: 'Confidential — access to individual responses is audited. Internal-client evaluators are anonymized.' })}</DialogDescription>
            </DialogHeader>
            {(raw?.evaluators || []).map((ev, i) => (
              <div key={i} className="space-y-2">
                <Badge variant="outline">{ev.label || `${t('talentperformance.eval.internalEvaluator', { defaultValue: 'Internal evaluator' })} ${ev.anonymous_index}`}</Badge>
                {(ev.items || []).map((it, j) => (
                  <div key={j} className="rounded border border-border/60 p-2.5 text-sm">
                    <p className="font-medium">{es ? it.prompt_es : it.prompt_en}</p>
                    <div className="text-muted-foreground mt-1">
                      {(it.selected_options || []).map((o, k) => <Badge key={k} variant="outline" className="mr-1.5">{es ? o.label_es : o.label_en}</Badge>)}
                      {it.text && <p className="whitespace-pre-wrap mt-1">{it.text}</p>}
                      {!it.text && (it.selected_options || []).length === 0 && <span>—</span>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </DialogContent>
        </Dialog>

        {/* Plan item dialog */}
        <Dialog open={planOpen} onOpenChange={setPlanOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="tp-plan-dialog">
            <DialogHeader><DialogTitle>{t('talentperformance.plan.add', { defaultValue: 'Add action' })}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t('talentperformance.profile.dimension', { defaultValue: 'Dimension' })}</Label>
                  <Select value={plan.dimension} onValueChange={(v) => setPlan((p) => ({ ...p, dimension: v }))}>
                    <SelectTrigger data-testid="tp-plan-dimension"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{dims.map((d) => <SelectItem key={d} value={d}>{dimLabel(t, d)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{t('talentperformance.plan.type', { defaultValue: 'Action type' })}</Label>
                  <Select value={plan.action_type} onValueChange={(v) => setPlan((p) => ({ ...p, action_type: v }))}>
                    <SelectTrigger data-testid="tp-plan-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{ACTION_TYPES.map((a) => <SelectItem key={a} value={a}>{t(`talentperformance.plan.types.${a}`, { defaultValue: a })}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>{t('talentperformance.plan.objective', { defaultValue: 'Objective' })}</Label>
                <Input value={plan.objective} onChange={(e) => setPlan((p) => ({ ...p, objective: e.target.value }))} data-testid="tp-plan-objective" />
              </div>
              <div className="space-y-1">
                <Label>{t('talentperformance.plan.action', { defaultValue: 'Action' })}</Label>
                <Input value={plan.action} onChange={(e) => setPlan((p) => ({ ...p, action: e.target.value }))} data-testid="tp-plan-action" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t('talentperformance.plan.responsible', { defaultValue: 'Responsible' })}</Label>
                  <Input value={plan.responsible} onChange={(e) => setPlan((p) => ({ ...p, responsible: e.target.value }))} data-testid="tp-plan-responsible" />
                </div>
                <div className="space-y-1">
                  <Label>{t('talentperformance.plan.target', { defaultValue: 'Target date' })}</Label>
                  <Input type="date" value={plan.target_date} onChange={(e) => setPlan((p) => ({ ...p, target_date: e.target.value }))} data-testid="tp-plan-target" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlanOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button disabled={busy || plan.objective.trim().length < 3 || plan.action.trim().length < 3}
                onClick={() => act(async () => {
                  await api.post(`${base}/plan`, { ...plan, dimension: plan.dimension || null, responsible: plan.responsible || null, target_date: plan.target_date || null });
                  setPlanOpen(false);
                  setPlan({ dimension: '', objective: '', action: '', action_type: 'training', responsible: '', target_date: '' });
                }, t('talentperformance.plan.addedOk', { defaultValue: 'Development action added.' }))}
                data-testid="tp-plan-submit">
                {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{t('common.add', { defaultValue: 'Add' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
